/**
 * ManifestDashboard — landing page for /manifest.
 *
 * Layout mirrors MyTenders: header with title + view toggle, split layout
 * with main content on the left and a "Needs attention" sidebar on the right.
 *
 * Two main views:
 *   - Board: 6-column kanban (one per workflow phase), contracts as cards
 *   - List: searchable table (the original view)
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Anchor, LayoutGrid, LayoutList, Search, RotateCcw,
  ArrowRight, AlertCircle, Clock, Wallet, TrendingUp, DollarSign,
} from 'lucide-react';
import { useManifest } from './ManifestContext';
import {
  PHASES, TARGET_TOTAL_DAYS, overallProgress, findCurrentStep, getPhase,
} from './fixtures';

const formatCad = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const dayDiff = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
};

// ---------- Kanban card ----------

const KanbanCard = ({ order }) => {
  const days = dayDiff(order.awardDate);
  const overdue = days > TARGET_TOTAL_DAYS;
  const { pct } = overallProgress(order.stepStates || {});
  const cur = findCurrentStep(order.stepStates || {});

  return (
    <Link to={`/manifest/${order.id}`} className="manifest-kanban-card">
      <div className="manifest-kanban-card-title">{order.contractTitle}</div>
      <div className="manifest-kanban-card-buyer">{order.buyer}</div>
      <div className="manifest-kanban-card-meta">
        <span className="manifest-kanban-card-value">{formatCad(order.contractValueCad)} CAD</span>
        <span className={`manifest-kanban-card-day ${overdue ? 'overdue' : ''}`}>
          Day {days}/{TARGET_TOTAL_DAYS}
          {overdue && <AlertCircle size={10} />}
        </span>
      </div>
      <div className="manifest-kanban-card-progress">
        <div className={`manifest-kanban-card-progress-fill phase-${cur.phase}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="manifest-kanban-card-next">
        <span className="manifest-kanban-card-next-label">Next:</span> {cur.title}
      </div>
    </Link>
  );
};

// ---------- Kanban board ----------

const KanbanBoard = ({ orders }) => {
  const ordersByPhase = useMemo(() => {
    const map = PHASES.reduce((acc, p) => { acc[p.id] = []; return acc; }, {});
    for (const o of orders) {
      const cur = findCurrentStep(o.stepStates || {});
      map[cur.phase] = [...(map[cur.phase] || []), o];
    }
    return map;
  }, [orders]);

  const totalsByPhase = useMemo(() => {
    const map = {};
    for (const [pid, list] of Object.entries(ordersByPhase)) {
      map[pid] = list.reduce((s, o) => s + (o.contractValueCad || 0), 0);
    }
    return map;
  }, [ordersByPhase]);

  return (
    <div className="manifest-kanban-board">
      {PHASES.map((p) => {
        const list = ordersByPhase[p.id] || [];
        return (
          <div key={p.id} className={`manifest-kanban-column phase-${p.id}`}>
            <div className="manifest-kanban-col-head">
              <div className="manifest-kanban-col-title-row">
                <span className={`manifest-kanban-col-dot phase-${p.id}`} />
                <span className="manifest-kanban-col-title">{p.label}</span>
                <span className="manifest-kanban-col-count">{list.length}</span>
              </div>
              <div className="manifest-kanban-col-window">{p.dayWindow}</div>
              <div className="manifest-kanban-col-total">{formatCad(totalsByPhase[p.id])}</div>
            </div>
            <div className="manifest-kanban-col-body">
              {list.map((o) => <KanbanCard key={o.id} order={o} />)}
              {list.length === 0 && (
                <div className="manifest-kanban-col-empty">No contracts in this phase</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------- List view (table) ----------

const ListView = ({ orders, searchTerm, setSearchTerm }) => {
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return orders;
    const q = searchTerm.toLowerCase();
    return orders.filter((o) =>
      (o.contractTitle || '').toLowerCase().includes(q) ||
      (o.buyer || '').toLowerCase().includes(q) ||
      (o.solicitationNumber || '').toLowerCase().includes(q),
    );
  }, [orders, searchTerm]);

  return (
    <div className="manifest-list-section">
      <form className="manifest-list-search" onSubmit={(e) => e.preventDefault()}>
        <Search className="manifest-list-search-icon" size={14} />
        <input
          type="search"
          placeholder="Search contracts, buyers, solicitation #…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </form>

      <div className="manifest-orders-table">
        <div className="manifest-orders-table-head">
          <div>Contract</div>
          <div>Buyer</div>
          <div>Value</div>
          <div>Phase</div>
          <div>Day</div>
          <div>Progress</div>
          <div>Next step</div>
          <div></div>
        </div>
        {filtered.map((o) => {
          const days = dayDiff(o.awardDate);
          const overdue = days > TARGET_TOTAL_DAYS;
          const { pct } = overallProgress(o.stepStates || {});
          const cur = findCurrentStep(o.stepStates || {});
          const phase = getPhase(cur.phase);
          return (
            <Link key={o.id} to={`/manifest/${o.id}`} className="manifest-orders-table-row">
              <div className="manifest-orders-cell-title">
                <strong>{o.contractTitle}</strong>
                <span className="manifest-orders-cell-sub">{o.solicitationNumber} · qty {o.quantity?.toLocaleString()}</span>
              </div>
              <div className="manifest-orders-cell-buyer">{o.buyer}</div>
              <div className="manifest-orders-cell-num">{formatCad(o.contractValueCad)} CAD</div>
              <div><span className={`manifest-mini-pill phase-${phase.id}`}>{phase.label}</span></div>
              <div className={`manifest-orders-cell-day ${overdue ? 'overdue' : ''}`}>
                {days} / {TARGET_TOTAL_DAYS}
                {overdue && <AlertCircle size={11} />}
              </div>
              <div className="manifest-orders-cell-progress">
                <div className="manifest-orders-progress-bar">
                  <div className={`manifest-orders-progress-fill phase-${phase.id}`} style={{ width: `${pct}%` }} />
                </div>
                <span>{pct}%</span>
              </div>
              <div className="manifest-orders-cell-next">{cur.title}</div>
              <div className="manifest-orders-cell-arrow"><ArrowRight size={16} /></div>
            </Link>
          );
        })}
        {filtered.length === 0 && (
          <div className="manifest-list-empty">No contracts match this search.</div>
        )}
      </div>
    </div>
  );
};

// ---------- Right sidebar — needs attention ----------

const NeedsAttention = ({ orders }) => {
  const navigate = useNavigate();

  const overdue = orders.filter((o) => dayDiff(o.awardDate) > TARGET_TOTAL_DAYS);

  // Payment pending: orders past production but step 24 not done
  const paymentPending = orders.filter((o) => {
    const states = o.stepStates || {};
    const cur = findCurrentStep(states);
    return cur.phase === 6 && (states[24]?.status !== 'done');
  });

  // Action required: orders where the current step has been "in_progress" for a while (demo: just take in_progress)
  const inProgress = orders.filter((o) => {
    const cur = findCurrentStep(o.stepStates || {});
    return cur.phase < 6;
  });

  return (
    <aside className="manifest-needs-attention">
      <div className="manifest-needs-card">
        <div className="manifest-needs-card-head">
          <Clock size={14} />
          <span>Active</span>
          <span className="manifest-needs-count">{inProgress.length}</span>
        </div>
        <div className="manifest-needs-card-list">
          {inProgress.length === 0 && <div className="manifest-needs-empty">All contracts complete.</div>}
          {inProgress.slice(0, 4).map((o) => {
            const cur = findCurrentStep(o.stepStates || {});
            const phase = getPhase(cur.phase);
            return (
              <button
                key={o.id}
                className="manifest-needs-row"
                onClick={() => navigate(`/manifest/${o.id}`)}
              >
                <div className="manifest-needs-row-title">{o.contractTitle}</div>
                <div className="manifest-needs-row-meta">
                  <span className={`manifest-mini-pill phase-${phase.id}`}>{phase.label}</span>
                  <span>Step {cur.id}: {cur.title}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {overdue.length > 0 && (
        <div className="manifest-needs-card warn">
          <div className="manifest-needs-card-head">
            <AlertCircle size={14} />
            <span>Overdue</span>
            <span className="manifest-needs-count danger">{overdue.length}</span>
          </div>
          <div className="manifest-needs-card-list">
            {overdue.map((o) => {
              const days = dayDiff(o.awardDate);
              return (
                <button
                  key={o.id}
                  className="manifest-needs-row danger"
                  onClick={() => navigate(`/manifest/${o.id}`)}
                >
                  <div className="manifest-needs-row-title">{o.contractTitle}</div>
                  <div className="manifest-needs-row-meta">
                    <span className="manifest-needs-overdue">+{days - TARGET_TOTAL_DAYS}d past target</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {paymentPending.length > 0 && (
        <div className="manifest-needs-card">
          <div className="manifest-needs-card-head">
            <Wallet size={14} />
            <span>Awaiting payment</span>
            <span className="manifest-needs-count">{paymentPending.length}</span>
          </div>
          <div className="manifest-needs-card-list">
            {paymentPending.map((o) => (
              <button
                key={o.id}
                className="manifest-needs-row"
                onClick={() => navigate(`/manifest/${o.id}`)}
              >
                <div className="manifest-needs-row-title">{o.contractTitle}</div>
                <div className="manifest-needs-row-meta">
                  <span className="manifest-needs-amount">{formatCad(o.contractValueCad)} CAD</span>
                  <span>Net-30 from invoice</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

// ---------- Main dashboard ----------

const ManifestDashboard = () => {
  const { orders, resetDemo } = useManifest();
  const [view, setView] = useState('list'); // 'board' | 'list'
  const [searchTerm, setSearchTerm] = useState('');

  const totalValue = orders.reduce((s, o) => s + (o.contractValueCad || 0), 0);
  const overdueCount = orders.filter((o) => dayDiff(o.awardDate) > TARGET_TOTAL_DAYS).length;
  const onTimePct = orders.length === 0 ? 100 : Math.round(((orders.length - overdueCount) / orders.length) * 100);
  const paymentPending = orders.reduce((s, o) => {
    const states = o.stepStates || {};
    const lastDone = Object.values(states).filter((x) => x?.status === 'done').length;
    return lastDone < 24 ? s + (o.contractValueCad || 0) : s;
  }, 0);

  return (
    <div className="manifest-page">
      {/* Page header — mirrors MyTenders */}
      <div className="manifest-pheader">
        <div className="manifest-pheader-content">
          <div className="manifest-pheader-title-row">
            <Anchor size={28} className="manifest-pheader-icon" />
            <div>
              <h1 className="manifest-pheader-title">
                Manifest <span className="preview-badge">Preview</span>
              </h1>
              <p className="manifest-pheader-subtitle">
                {orders.length} active contract{orders.length !== 1 ? 's' : ''} · {formatCad(totalValue)} CAD in flight · From contract win to government payment.
              </p>
            </div>
          </div>
        </div>

        <div className="manifest-pheader-actions">
          <div className="manifest-pheader-toggle">
            <button
              className={`manifest-pheader-toggle-btn ${view === 'board' ? 'active' : ''}`}
              onClick={() => setView('board')}
              title="Board view"
            >
              <LayoutGrid size={14} /> Board
            </button>
            <button
              className={`manifest-pheader-toggle-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
              title="List view"
            >
              <LayoutList size={14} /> List
            </button>
          </div>
          <button className="manifest-pheader-action-btn" onClick={resetDemo} title="Reset demo state">
            <RotateCcw size={14} /> Reset demo
          </button>
        </div>
      </div>

      {/* Split layout — main + sidebar */}
      <div className="manifest-split">
        <div className="manifest-split-main">
          {/* Compact stats strip — sits inside main column so it aligns with kanban */}
          <div className="manifest-stats">
            <div className="manifest-stat-card accent-active">
              <div className="manifest-stat-card-row">
                <div className="manifest-stat-icon"><TrendingUp size={14} /></div>
                <div className="manifest-stat-card-label">Active</div>
              </div>
              <div className="manifest-stat-card-value">{orders.length}</div>
              <div className="manifest-stat-card-sub">contract{orders.length !== 1 ? 's' : ''} in flight</div>
            </div>
            <div className="manifest-stat-card accent-value">
              <div className="manifest-stat-card-row">
                <div className="manifest-stat-icon"><DollarSign size={14} /></div>
                <div className="manifest-stat-card-label">Total value</div>
              </div>
              <div className="manifest-stat-card-value">{formatCad(totalValue)} <span className="manifest-stat-card-unit">CAD</span></div>
              <div className="manifest-stat-card-sub">across {orders.length} contract{orders.length !== 1 ? 's' : ''}</div>
            </div>
            <div className="manifest-stat-card accent-time">
              <div className="manifest-stat-card-row">
                <div className="manifest-stat-icon"><Clock size={14} /></div>
                <div className="manifest-stat-card-label">On-time</div>
              </div>
              <div className="manifest-stat-card-value">{onTimePct}<span className="manifest-stat-card-unit">%</span></div>
              <div className="manifest-stat-card-sub">
                {overdueCount === 0 ? 'all within target' : `${overdueCount} overdue`}
              </div>
            </div>
            <div className="manifest-stat-card accent-payment">
              <div className="manifest-stat-card-row">
                <div className="manifest-stat-icon"><Wallet size={14} /></div>
                <div className="manifest-stat-card-label">Payment pending</div>
              </div>
              <div className="manifest-stat-card-value">{formatCad(paymentPending)} <span className="manifest-stat-card-unit">CAD</span></div>
              <div className="manifest-stat-card-sub">net-30 from invoice</div>
            </div>
          </div>

          {view === 'board' ? (
            <KanbanBoard orders={orders} />
          ) : (
            <ListView orders={orders} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          )}
        </div>
        <NeedsAttention orders={orders} />
      </div>
    </div>
  );
};

export default ManifestDashboard;
